import React, { useEffect, useState } from "react";
import { PageLayout } from "../../components";
import { useSelector, useDispatch } from "react-redux";
import { getPhotoData, helperTextObject } from "../helpers";
import PhotosRequiredList from "./PhotosRequiredList";
import {
  allRequiredPhotosUploaded,
  hasEnoughPhotosUploaded
} from "../ImageUploads/helpers";
import {
  getAppraisalData,
  getNextPhotoGroup,
  getSourceSystemIsBranchAppraisal,
  getSourceSystemIsCompactView,
  selectImageUploadData
} from "../../store/selectors";
import { imageUploadSlice } from "../../store/slices/ImageUploads";
import { useRetryRequest } from "../../hooks/useRetryRequest";
import { setSessionStorage } from "../../sessionAndLocalStorage";
import { useNavigate } from "react-router-dom";
import { MinPhotosRequired } from "../../data/requiredPhotoData";

export default function PhotosIntro() {
  const [sentences, setSentences] = useState([]);
  const {
    questionsAndAnswers,
    photosRequired: requiredCategories,
    images: appraisalImages,
    quickValuationPartExchange
  } = useSelector(getAppraisalData);
  const { images, requiredPhotoData } = useSelector(selectImageUploadData);
  const firstGroup = useSelector(getNextPhotoGroup);
  const isBranchAppraisal = useSelector(getSourceSystemIsBranchAppraisal);
  const isCompactView = useSelector(getSourceSystemIsCompactView);
  const journeyType = isBranchAppraisal ? "Branch" : "Customer";
  const allImagesUploaded = allRequiredPhotosUploaded(
    images,
    requiredPhotoData
  );
  const minImagesUploaded = images.length >= MinPhotosRequired;
  const dispatch = useDispatch();
  const { appraisalRequest } = useRetryRequest();
  const navigate = useNavigate();

  useEffect(() => {
    if (requiredCategories && Object.keys(requiredCategories).length > 0) {
      for (const [key, value] of Object.entries(requiredCategories)) {
        if (value > 0) {
          const answerId = questionsAndAnswers[key][0];
          const photoData = getPhotoData(key, value, answerId);
          photoData.questionId = key;
          const order = photoData.order;

          if (!hasEnoughPhotosUploaded(images, photoData)) {
            const sentenceObject = {
              [order]: photoData.photoIntroText
            };
            setSentences((sentences) => [...sentences, sentenceObject]);
          }
          dispatch(imageUploadSlice.actions.setRequiredPhotoData(photoData));
        }
      }
    }
    // eslint-disable-next-line
  }, [dispatch, questionsAndAnswers, requiredCategories]);

  function reviewAnswers() {
    navigate("/review-answers");
  }

  const requestAppraisalWithRetry = () => {
    appraisalRequest();
  };

  useEffect(requestAppraisalWithRetry, []);

  useEffect(() => {
    if (images.length) {
      dispatch(imageUploadSlice.actions.clearImages());
    }
    // eslint-disable-next-line
    const formattedImages = appraisalImages?.map((img) => {
      if (img.appraiser === journeyType) {
        const tagDescription =
          img.imageTags[img.imageTags.length - 1]?.tagDescription;
        const formattedImg = {
          imageIdentifier: img.imageIdentifier,
          // Fix for label not being parsed correctly when has spaces by BE
          group:
            tagDescription === "Mechanical "
              ? "Mechanical & Electrical"
              : tagDescription,
          imageSrc: img.imageIdentifier,
          progress: 100,
          helperText: helperTextObject[tagDescription]
        };
        dispatch(imageUploadSlice.actions.setImages(formattedImg));
        return formattedImg;
      }
    });
    const includedUndefined = formattedImages.some((img) => !img);
    if (!includedUndefined) {
      setSessionStorage("images", formattedImages);
    }
  }, [dispatch, appraisalImages, journeyType, images.length]);

  const handleClick = () => {
    if (firstGroup) {
      return navigate(`/image-upload/${firstGroup.id}`);
    }
    if (quickValuationPartExchange) {
      return navigate("/photo-gallery");
    }
    if (allImagesUploaded && minImagesUploaded) {
      return navigate("/estimated-offer");
    }
    return navigate("/error");
  };

  const analytics = {
    "data-gtm-track": "interaction",
    "data-gtm-track-label": "Review answers"
  };

  return (
    <PageLayout>
      <PageLayout.Header
        title="Upload photographs"
        step={isCompactView ? 5 : 11}
      ></PageLayout.Header>
      <PageLayout.Content>
        <div className="ch-display--flex ch-flex-flow--column-wrap">
          <p className="ch-fs--4">
            We'll guide you through each photo but here's a heads up of what we
            will need.
          </p>
          <div className="ch-card ch-bg--white ch-bc--grey-3 ch-rounded">
            <div
              className={`${
                allImagesUploaded || !quickValuationPartExchange
                  ? "photos-intro-container"
                  : "photos-intro-container--quick-valuation"
              }`}
            >
              <PhotosRequiredList sentences={sentences} />
              {!quickValuationPartExchange && (
                <>
                  <div className="ch-bb--1 ch-bc--grey-3 divider" />
                  <div className="ch-fs--2 ch-text--center ch-mt--0 ch-lh--base">
                    <p className="ch-mb--0 ch-color--grey-6">
                      Here's what you've told us
                    </p>
                    <button
                      className="ch-btn ch-fs--2 ch-btn--link ch-text--center ch-pa--0 ch-pt--1"
                      {...analytics}
                      onClick={reviewAnswers}
                    >
                      Review your answers
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <PageLayout.Footer>
        <button
          className="ch-btn ch-btn--success ch-display--block ch-mh--auto sm:ch-width--6 md:ch-width--5 ch-width--12 lg:ch-width--4"
          type="button"
          onClick={handleClick}
          data-testid="photos-intro-btn"
        >
          {images.length > 0 ? "Continue" : "Start"}
        </button>
      </PageLayout.Footer>
    </PageLayout>
  );
}
